import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { Context as GeneralContext } from '@/context'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import Certifications from '@/components/Certifications'
import Footer from '@/components/Footer'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import { cutExcerpt, insertBrAfterFirstSpace } from '@/utils/strings'
import style from './style'

const useStyles = createUseStyles(style)

const Certificazioni = ({
  stub,
}) => {
  const { setPageAnimationReady, setHeaderInverse } = useContext(GeneralContext)
  const classes = useStyles()
  const [load, setLoad] = useState(false)
  const $root = useRef()

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub] || {},
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    getPage(stub)
  }

  /*------------------------------
  Set Header Logo Styles
  ------------------------------*/
  useEffect(() => {
    setHeaderInverse(true)
    return () => setHeaderInverse(false)
  }, [])

  /*------------------------------
  If the page is loaded from redux, start to preload media
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) completePreload()
  }, [page])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load ? (
      <Meta head_json={page.yoast_head} />
    ) : null
  }

  /*------------------------------
  Render Heading
  ------------------------------*/
  const renderHeading = () => {
    return (
      <Hero
        aboveTheFold
        viewportHeight={false}
        background="light"
        fontSize="md"
        text={cutExcerpt(page.content.rendered)}
        title={insertBrAfterFirstSpace(page.title.rendered)}
        imgSize="crop"
        onComplete={() => setPageAnimationReady(true)}
      />
    )
  }

  /*------------------------------
  Render Certificazioni
  ------------------------------*/
  const renderCertifications = () => {
    return page.acf?.certificazioni?.length > 0 ? (
      <Certifications items={page.acf.certificazioni} />
    ) : null
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer theme="dark" />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderHeading()}
        {renderCertifications()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageCertificazioni ${stub} ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default Certificazioni
